import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../config/firebase';
import type { User } from 'firebase/auth';
import { initPush } from '../services/pushNotificationService';
import { initWebPush } from '../services/webPushService';

interface AuthContextType {
  user: User | null | undefined;
  loading: boolean;
  error: Error | undefined;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, loading, error] = useAuthState(auth);

  // Register this client for push notifications once logged in. initPush = native app (no-ops on web);
  // initWebPush = silent web re-register for users who ALREADY granted permission (no-ops otherwise —
  // the first-time web prompt is the explicit EnableNotificationsCard tap on the dashboard).
  useEffect(() => { if (user?.uid) { initPush(user.uid); initWebPush(user.uid); } }, [user?.uid]);

  return (
    <AuthContext.Provider value={{ user, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
